import React, { useState, useEffect } from 'react';
import './BudgetDetailModal.css';

interface FiscalData {
  value: number;
  fiscal_year: number;
  fiscal_type: string;
}

interface CostItem {
  cost_item_name: string;
  standard_name?: string;
  spending_category?: string;
  description?: string;
  scost_item_supplier?: string;
  fiscal_data: FiscalData[];
}

interface Project {
  pjid: number;
  project_name: string;
  project_type?: string;
  project_description?: string;
  cost_items: CostItem[];
}

interface SearchResult {
  id: string;
  document_id?: number;
  peid?: number;
  line_item?: string;
  line_item_title: string;
  line_item_description?: string;
  organization: string;
  account?: string;
  account_title?: string;
  budget_activity_title?: string;
  program?: string;
  domain?: string;
  supplier?: string;
  projects: Project[];
  total_budget?: number;
  fiscal_years: number[];
  relevance_score?: number;
  highlight?: Record<string, string[]>;
  title: string;
  description: string;
  agency: string;
  budget?: number;
  category: string;
  status: string;
}

interface BudgetDetailModalProps {
  result: SearchResult | null;
  isOpen: boolean;
  onClose: () => void;
}

const BudgetDetailModal: React.FC<BudgetDetailModalProps> = ({ result, isOpen, onClose }) => {
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [expandedCostItems, setExpandedCostItems] = useState<Set<string>>(new Set());

  // Highlight utility function
  const renderHighlightedText = (text: string, fieldName: string): JSX.Element => {
    // Check if we have highlights for this field
    const highlights = result?.highlight?.[fieldName];
    
    if (!highlights || highlights.length === 0) {
      // No highlights, return plain text
      return <span>{text}</span>;
    }

    // Use the first highlight (which should contain the matched text with <em> tags)
    const highlightedText = highlights[0];
    
    // Parse the highlighted text to create React elements
    const parts = highlightedText.split(/(<em>.*?<\/em>)/g);
    
    const elements = parts.map((part, index) => {
      if (part.startsWith('<em>') && part.endsWith('</em>')) {
        // This is a highlighted part
        const content = part.replace(/<\/?em>/g, '');
        return <mark key={index} className="search-highlight">{content}</mark>;
      }
      return part;
    });
    
    return <span>{elements}</span>;
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !result) return null;

  const formatCurrency = (amount?: number) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getFiscalYearSummary = () => {
    if (!result.fiscal_years.length) return '';
    const years = result.fiscal_years.sort();
    if (years.length === 1) return `FY${years[0]}`;
    return `FY${years[0]}-${years[years.length - 1]}`;
  };

  const toggleProject = (projectId: number) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
      // Also collapse cost items for this project
      const costItemKey = `project-${projectId}`;
      const newExpandedCostItems = new Set(expandedCostItems);
      newExpandedCostItems.delete(costItemKey);
      setExpandedCostItems(newExpandedCostItems);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const toggleCostItems = (projectId: number) => {
    const key = `project-${projectId}`;
    const newExpanded = new Set(expandedCostItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedCostItems(newExpanded);
  };

  const getCostItemTotal = (costItem: CostItem): number => {
    return costItem.fiscal_data.reduce((sum, fiscal) => sum + fiscal.value, 0);
  };

  const getProjectTotal = (project: Project): number => {
    return project.cost_items.reduce((sum, item) => sum + getCostItemTotal(item), 0);
  };

  const expandAll = () => {
    const allProjectIds = result.projects.map(p => p.pjid);
    setExpandedProjects(new Set(allProjectIds));
  };

  const collapseAll = () => {
    setExpandedProjects(new Set());
    setExpandedCostItems(new Set());
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-content">
            <h2>{renderHighlightedText(result.line_item_title, 'line_item_title')}</h2>
            <div className="modal-meta">
              <span className="modal-org">{result.organization}</span>
              {result.account_title && <span>• {result.account_title}</span>}
              {result.program && <span>• {result.program}</span>}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Summary Section */}
          <div className="detail-section">
            <h3>Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <label>Total Budget</label>
                <span className="value">{formatCurrency(result.total_budget)}</span>
              </div>
              <div className="summary-item">
                <label>Fiscal Years</label>
                <span className="value">{getFiscalYearSummary()}</span>
              </div>
              <div className="summary-item">
                <label>Domain</label>
                <span className="value">{result.domain || 'N/A'}</span>
              </div>
              <div className="summary-item">
                <label>Projects</label>
                <span className="value">{result.projects.length}</span>
              </div>
            </div>
            
            {result.line_item_description && (
              <div className="description-section">
                <label>Description</label>
                <p>{renderHighlightedText(result.line_item_description, 'line_item_description')}</p>
              </div>
            )}
          </div>

          {/* Projects Section */}
          {result.projects.length > 0 && (
            <div className="detail-section">
              <div className="section-header">
                <h3>Projects ({result.projects.length})</h3>
                <div className="section-actions">
                  <button onClick={expandAll} className="text-btn">Expand All</button>
                  <button onClick={collapseAll} className="text-btn">Collapse All</button>
                </div>
              </div>

              <div className="projects-list">
                {result.projects.map((project) => (
                  <div key={project.pjid} className="project-item">
                    <div 
                      className="project-header"
                      onClick={() => toggleProject(project.pjid)}
                    >
                      <div className="project-info">
                        <span className="expand-icon">
                          {expandedProjects.has(project.pjid) ? '▼' : '▶'}
                        </span>
                        <div>
                          <h4>{renderHighlightedText(project.project_name, 'projects.project_name')}</h4>
                          {project.project_type && (
                            <span className="project-type">{project.project_type}</span>
                          )}
                        </div>
                      </div>
                      <div className="project-stats">
                        <span className="stat-badge">{project.cost_items.length} items</span>
                        <span className="project-total">{formatCurrency(getProjectTotal(project))}</span>
                      </div>
                    </div>

                    {expandedProjects.has(project.pjid) && (
                      <div className="project-content">
                        {project.project_description && (
                          <p className="project-description">{renderHighlightedText(project.project_description, 'projects.project_description')}</p>
                        )}

                        {project.cost_items.length > 0 && (
                          <div className="cost-items-section">
                            <div 
                              className="cost-items-header"
                              onClick={() => toggleCostItems(project.pjid)}
                            >
                              <span className="expand-icon">
                                {expandedCostItems.has(`project-${project.pjid}`) ? '▼' : '▶'}
                              </span>
                              <span>Cost Items ({project.cost_items.length})</span>
                            </div>

                            {expandedCostItems.has(`project-${project.pjid}`) && (
                              <div className="cost-items-list">
                                {project.cost_items.map((item, idx) => (
                                  <div key={idx} className="cost-item">
                                    <div className="cost-item-header">
                                      <div>
                                        <h5>{renderHighlightedText(item.cost_item_name, 'projects.cost_items.cost_item_name')}</h5>
                                        {item.spending_category && (
                                          <span className="cost-category">{item.spending_category}</span>
                                        )}
                                      </div>
                                      <span className="cost-amount">
                                        {formatCurrency(getCostItemTotal(item))}
                                      </span>
                                    </div>
                                    {item.description && (
                                      <p className="cost-description">{renderHighlightedText(item.description, 'projects.cost_items.description')}</p>
                                    )}
                                    {item.fiscal_data.length > 0 && (
                                      <div className="fiscal-breakdown">
                                        {item.fiscal_data.map((fiscal, fidx) => (
                                          <div key={fidx} className="fiscal-item">
                                            <span>FY{fiscal.fiscal_year}</span>
                                            <span>{formatCurrency(fiscal.value)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default BudgetDetailModal;